import useAmenitiesFilterOptions from "@/modules/common/hooks/lookups/amenities-filter-options.hook";
import useCompoundsFilterOptions from "@/modules/common/hooks/lookups/compounds-filter-options.hook";
import usePropertyTypesFilterOptions from "@/modules/common/hooks/lookups/property-types-filter-options.hook";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";

import Textarea from "@/modules/common/components/input/text-area";
import { FinishingTypes } from "@/modules/property-tracks/types";
import { PROPERTY_TRACKS } from "@/modules/property-tracks/utils";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import StyledTextField from "@/modules/common/components/input/styled-text-field";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { format } from "date-fns";

interface PropertyDetailsFormProps {
  form: UseFormReturn<any>;
  namePrefix?: string;
  disabledFields?: string[];
  trackTypeName?: string;
  buildingAmenities?: { id: number; name: string }[];
  isSubProperty?: boolean;
}

export default function PropertyDetailsForm({
  form: { control, watch },
  namePrefix,
  disabledFields,
  buildingAmenities,
  trackTypeName,
}: PropertyDetailsFormProps) {
  const { propertyTypesLoading, propertyTypes } =
    usePropertyTypesFilterOptions();

  const { compounds, compoundsLoading, handleSearchChange } =
    useCompoundsFilterOptions();

  const { amenities } = useAmenitiesFilterOptions();

  const visibleAmenities =
    trackTypeName === PROPERTY_TRACKS.HOSPITALITY
      ? amenities
      : amenities?.filter((amenity) => amenity.enablePropertyManagement);

  const theme = useTheme();

  const fieldName = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : field;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
        }}
      >
        <Controller
          control={control}
          name={fieldName("compound")}
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              value={field.value}
              options={
                compounds?.map((compound) => ({
                  label: compound.name,
                  value: compound.id?.toString() || "",
                })) || []
              }
              onChange={(value) => field.onChange(value)}
              handleSearch={handleSearchChange}
              loading={compoundsLoading}
              label="Compound"
              placeholder="Compound"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={disabledFields?.includes("compound")}
            ></CustomAutoComplete>
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("propertyType")}
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              value={field.value}
              options={
                propertyTypes?.map((type) => ({
                  label: type.name,
                  value: type.id?.toString() || "",
                })) || []
              }
              onChange={(value) => field.onChange(value)}
              loading={propertyTypesLoading}
              label="Property Type"
              placeholder="Property Type"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            ></CustomAutoComplete>
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("unitId")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                field.onChange(e.target.value.replace(/[^\x20-\x7E]/g, ""))
              }
              title="Unit Number"
              placeholder="Unit Number"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>

        <Controller
          control={control}
          name={fieldName("phase")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Phase"
              placeholder="Phase"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>

        <Controller
          control={control}
          name={fieldName("unitArea")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Buildup Area"
              placeholder="Buildup Area"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>m²</Typography>,
                },
              }}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("landArea")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Land Area"
              placeholder="Land Area"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>m²</Typography>,
                },
              }}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("gardenArea")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Garden Area"
              placeholder="Garden Area"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>m²</Typography>,
                },
              }}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("roofArea")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Roof Area"
              placeholder="Roof Area"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>m²</Typography>,
                },
              }}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("floorNumber")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Floor"
              placeholder="Floor"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("numberOfFloors")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Number of floors"
              placeholder="Number of floors"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("numberOfBedrooms")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Bedrooms"
              placeholder="Bedrooms"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("numberOfBathrooms")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Bathrooms"
              placeholder="Bathrooms"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <CustomLabel>
                <Typography
                  sx={(theme) =>
                    theme.nawy.typography.english.variants.body.xs.regular
                  }
                >
                  Delivery
                </Typography>
              </CustomLabel>
            </Box>
            <Controller
              control={control}
              name={fieldName("readyToMove")}
              render={({ field }) => (
                <FormControlLabel
                  {...field}
                  control={<Switch size="small" checked={field.value} />}
                  label={
                    <Typography
                      sx={(theme) =>
                        theme.nawy.typography.english.variants.body.xs.regular
                      }
                    >
                      Ready to move
                    </Typography>
                  }
                  labelPlacement="start"
                />
              )}
            />
          </Box>
          {!watch(fieldName("readyToMove")) && (
            <Controller
              control={control}
              name={fieldName("deliveryDate")}
              render={({ field, fieldState }) => (
                <StyledTextField
                  {...field}
                  value={
                    field.value
                      ? format(new Date(field.value), "yyyy-MM-dd")
                      : undefined
                  }
                  type="date"
                  placeholder="Delivery"
                  fullWidth
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            ></Controller>
          )}
        </Box>
        <Controller
          control={control}
          name={fieldName("lastInstallmentDate")}
          render={({ field, fieldState }) => (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomLabel>
                <Typography
                  sx={(theme) =>
                    theme.nawy.typography.english.variants.body.xs.regular
                  }
                >
                  Last Installment Date
                </Typography>
              </CustomLabel>
              <StyledTextField
                {...field}
                value={
                  field.value
                    ? format(new Date(field.value), "yyyy-MM-dd")
                    : undefined
                }
                type="date"
                placeholder="Last Installment Date"
                fullWidth
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </Box>
          )}
        ></Controller>
        <Controller
          control={control}
          name={fieldName("finishing")}
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              value={field.value}
              options={Object.entries(FinishingTypes).map(([key, value]) => ({
                label: value,
                value: key,
              }))}
              onChange={(value) => field.onChange(value)}
              placeholder="Finishing"
              label="Finishing"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        ></Controller>

        <Controller
          control={control}
          name={fieldName("latitude")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Latitude"
              placeholder="Latitude"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name={fieldName("longitude")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Longitude"
              placeholder="Longitude"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name={fieldName("address")}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Address"
              placeholder="Address"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        {buildingAmenities && buildingAmenities.length > 0 && (
          <Box>
            <Typography
              sx={(theme) =>
                theme.nawy.typography.english.variants.body.xs.semibold
              }
            >
              Building Amenities
            </Typography>
            <FormGroup>
              {buildingAmenities.map((amenity) => (
                <FormControlLabel
                  key={`building-${amenity.id}`}
                  control={<Checkbox checked disabled />}
                  label={amenity.name}
                />
              ))}
            </FormGroup>
          </Box>
        )}
        <Controller
          control={control}
          name={fieldName("amenityIds")}
          render={({ field }) => (
            <Box>
              <FormGroup>
                {visibleAmenities?.map((amenity) => (
                  <FormControlLabel
                    key={amenity.id}
                    control={
                      <Checkbox
                        checked={field.value?.has(amenity.id) || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const newValue = new Set(field.value ?? []);
                          if (checked) {
                            newValue.add(amenity.id);
                          } else {
                            newValue.delete(amenity.id);
                          }
                          field.onChange(newValue);
                        }}
                      />
                    }
                    label={amenity.name}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        />
        <Controller
          control={control}
          name={fieldName("description")}
          render={({ field, fieldState }) => (
            <Textarea
              value={field.value}
              onValueChange={field.onChange}
              label="Description"
              placeholder="Description"
              isError={!!fieldState.error}
              errorMsg={fieldState.error?.message}
            />
          )}
        ></Controller>
      </Box>
    </>
  );
}
